import { useTranslation } from 'react-i18next';
import { RadioGroup, Widget } from '@asset-metrix/portal-ui';
import { Stack, Text } from '@mantine/core';

import {
  createDataSourceConfiguration,
  InvestmentComplianceLimitDataLevel,
  isFilterOnlyLimit,
  LimitMode,
  LimitParameter,
  valueTypeOptions,
} from './createInvestmentLimitModel';
import { DataSourceSelection } from './DataSourceSelection';
import { valueTypeCards } from './investmentLimitLabels';
import { LimitExpressionFields } from './LimitExpressionFields';

import styles from './createInvestmentLimitModal.module.scss';

export type ParameterStepProps = {
  contentId: string;
  dataLevels: InvestmentComplianceLimitDataLevel[];
  includeDenominator: boolean;
  limitMode: LimitMode;
  onParameterChange: (update: Partial<LimitParameter>) => void;
  parameter: LimitParameter;
};

export function ParameterStep({
  contentId,
  dataLevels,
  includeDenominator,
  limitMode,
  onParameterChange,
  parameter,
}: ParameterStepProps) {
  const { t } = useTranslation();

  return (
    <Stack className={styles['modal-step']}>
      {!isFilterOnlyLimit(limitMode) && (
        <Widget title={t('app.investmentLimit.widgets.comparisonType')}>
          <div className={styles['modal-card-content']}>
            <RadioGroup
              direction="row"
              name="investment-limit-comparison-type"
              onChange={(value) => {
                const valueType = valueTypeOptions.find(
                  (option) => option.value === value
                )?.value;
                if (!valueType) {
                  return;
                }
                onParameterChange({
                  valueType,
                  denominator:
                    valueType === 'relative'
                      ? (parameter.denominator ??
                        createDataSourceConfiguration())
                      : null,
                });
              }}
              options={valueTypeCards(t)}
              value={parameter.valueType}
              variant="cards"
            />
          </div>
        </Widget>
      )}
      <Widget title={t('app.investmentLimit.widgets.limitParameters')}>
        <div className={styles['modal-card-content']}>
          <DataSourceSelection
            contentId={contentId}
            dataLevels={dataLevels}
            onChange={onParameterChange}
            showSectionTitle={false}
            source={parameter}
          />
        </div>
      </Widget>
      <Widget title={t('app.investmentLimit.widgets.evaluation')}>
        <div className={styles['modal-card-content']}>
          {isFilterOnlyLimit(limitMode) ? (
            <Text className={styles['field-hint']}>
              {limitMode === 'Inclusion'
                ? t('app.investmentLimit.preview.inclusionBreachHint')
                : t('app.investmentLimit.preview.exclusionBreachHint')}
            </Text>
          ) : includeDenominator ? (
            <Text className={styles['field-hint']}>
              {t('app.investmentLimit.preview.denominatorFieldsHint')}
            </Text>
          ) : (
            <LimitExpressionFields
              limitMode={limitMode}
              onChange={onParameterChange}
              parameter={parameter}
            />
          )}
        </div>
      </Widget>
    </Stack>
  );
}
